import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MeetingBooksLinkApiService } from '../../services/meeting-books-link-api.service';
import {
  ConditionalLogicOperator,
  ConditionalLogicRule,
  FormFieldType,
  MeetingBookingFormFieldRequest,
  MeetingBookingFormFieldResponse,
  FORM_FIELD_TYPE_LABELS,
  meetingLabel,
} from '../../models/meeting.model';
import { ConfirmDialogService } from '../../../../shared/services/confirm-dialog.service';
import { ToastService } from '../../../../shared/services/toast.service';

@Component({
  selector: 'app-booking-form-fields',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './booking-form-fields.component.html',
})
export class BookingFormFieldsComponent implements OnChanges {
  @Input() bookingLinkId!: number;

  readonly fieldTypes: FormFieldType[] = [
    'text', 'textarea', 'email', 'phone', 'dropdown', 'checkbox', 'radio', 'hidden', 'number',
  ];

  // INC 38 — operadores de lógica condicional disponibles en la UI
  readonly conditionalOperators: { value: ConditionalLogicOperator; label: string }[] = [
    { value: 'equals', label: 'es igual a' },
    { value: 'notEquals', label: 'es distinto de' },
    { value: 'contains', label: 'contiene' },
  ];

  fieldTypeLabel(type: string | null | undefined): string {
    return meetingLabel(FORM_FIELD_TYPE_LABELS, type);
  }

  items: MeetingBookingFormFieldResponse[] = [];
  loading = false;
  saving = false;

  // Alta
  newName = '';
  newLabel = '';
  newType: FormFieldType = 'text';
  newRequired = false;
  newPlaceholder = '';
  newOptions = '';
  newCrmProperty = '';

  // INC 38 — lógica condicional del nuevo campo (alta)
  newConditionalEnabled = false;
  newConditionalField = '';
  newConditionalOperator: ConditionalLogicOperator = 'equals';
  newConditionalValue = '';

  // INC 38 — edición de la lógica condicional de un campo existente
  editingConditionalId: number | null = null;
  editConditionalEnabled = false;
  editConditionalField = '';
  editConditionalOperator: ConditionalLogicOperator = 'equals';
  editConditionalValue = '';

  constructor(
    private api: MeetingBooksLinkApiService,
    private confirmDialog: ConfirmDialogService,
    private toast: ToastService
  ) {}

  ngOnChanges(c: SimpleChanges): void {
    if (c['bookingLinkId'] && this.bookingLinkId) this.load();
  }

  needsOptions(type: FormFieldType | string): boolean {
    return type === 'dropdown' || type === 'radio' || type === 'checkbox';
  }

  load(): void {
    this.loading = true;
    this.api.listFormFields(this.bookingLinkId).subscribe({
      next: (data) => {
        this.items = [...data].sort(
          (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)
        );
        this.loading = false;
      },
      error: () => (this.loading = false),
    });
  }

  /** INC 38 — otros campos que pueden usarse como condición (excluye el nuevo). */
  get availableConditionFields(): MeetingBookingFormFieldResponse[] {
    return this.items;
  }

  add(): void {
    if (!this.newName.trim() || !this.newLabel.trim()) return;
    this.saving = true;
    const body: MeetingBookingFormFieldRequest = {
      fieldName: this.newName.trim(),
      fieldLabel: this.newLabel.trim(),
      fieldType: this.newType,
      isRequired: this.newRequired,
      sortOrder: this.items.length,
      placeholder: this.newPlaceholder?.trim() || undefined,
      options: this.needsOptions(this.newType) ? this.newOptions?.trim() || undefined : undefined,
      crmProperty: this.newCrmProperty?.trim() || undefined,
      // INC 38 — serializar la regla de visibilidad si está habilitada
      conditionalLogic: this.serializeConditional(
        this.newConditionalEnabled,
        this.newConditionalField,
        this.newConditionalOperator,
        this.newConditionalValue
      ),
      isActive: true,
    };
    this.api.createFormField(this.bookingLinkId, body).subscribe({
      next: () => {
        this.saving = false;
        this.newName = '';
        this.newLabel = '';
        this.newPlaceholder = '';
        this.newOptions = '';
        this.newCrmProperty = '';
        this.newRequired = false;
        this.newType = 'text';
        this.newConditionalEnabled = false;
        this.newConditionalField = '';
        this.newConditionalOperator = 'equals';
        this.newConditionalValue = '';
        this.load();
        this.toast.created('Campo');
      },
      error: (err) => {
        this.saving = false;
        this.toast.error({ title: 'Error', message: err.error?.message || 'Error al crear campo' });
      },
    });
  }

  // ─── INC 38: lógica condicional ────────────────────────────────────────────

  /** Parsea el JSON de `conditionalLogic` a una regla, o null si no hay/está mal formado. */
  parseConditional(raw?: string): ConditionalLogicRule | null {
    if (!raw || !raw.trim()) return null;
    try {
      const obj = JSON.parse(raw);
      if (obj && typeof obj === 'object' && obj.field && obj.operator) {
        return {
          field: String(obj.field),
          operator: obj.operator as ConditionalLogicOperator,
          value: obj.value != null ? String(obj.value) : '',
        };
      }
    } catch {
      // JSON inválido o formato legado → tratar como sin regla
    }
    return null;
  }

  /** Serializa una regla a JSON string, o undefined si no está habilitada/completa. */
  private serializeConditional(
    enabled: boolean,
    field: string,
    operator: ConditionalLogicOperator,
    value: string
  ): string | undefined {
    if (!enabled || !field?.trim()) return undefined;
    const rule: ConditionalLogicRule = {
      field: field.trim(),
      operator,
      value: (value ?? '').trim(),
    };
    return JSON.stringify(rule);
  }

  /** Etiqueta visible de un campo por su fieldName (para el resumen de la condición). */
  fieldLabelByName(fieldName: string): string {
    return this.items.find((i) => i.fieldName === fieldName)?.fieldLabel || fieldName;
  }

  operatorLabel(op: string): string {
    return this.conditionalOperators.find((o) => o.value === op)?.label || op;
  }

  /** Texto legible de la condición de un campo, o '' si no tiene. */
  conditionalSummary(item: MeetingBookingFormFieldResponse): string {
    const rule = this.parseConditional(item.conditionalLogic);
    if (!rule) return '';
    return `Visible si "${this.fieldLabelByName(rule.field)}" ${this.operatorLabel(rule.operator)} "${rule.value}"`;
  }

  /** Abre el editor de condición para un campo, precargando su regla actual. */
  startEditConditional(item: MeetingBookingFormFieldResponse): void {
    this.editingConditionalId = item.id;
    const rule = this.parseConditional(item.conditionalLogic);
    this.editConditionalEnabled = !!rule;
    this.editConditionalField = rule?.field || '';
    this.editConditionalOperator = rule?.operator || 'equals';
    this.editConditionalValue = rule?.value || '';
  }

  cancelEditConditional(): void {
    this.editingConditionalId = null;
  }

  /** Campos que pueden usarse como condición de `item` (cualquiera distinto de él). */
  conditionFieldsFor(item: MeetingBookingFormFieldResponse): MeetingBookingFormFieldResponse[] {
    return this.items.filter((i) => i.fieldName !== item.fieldName);
  }

  /** Guarda la condición editada reutilizando el update completo del campo. */
  saveConditional(item: MeetingBookingFormFieldResponse): void {
    const conditionalLogic = this.serializeConditional(
      this.editConditionalEnabled,
      this.editConditionalField,
      this.editConditionalOperator,
      this.editConditionalValue
    );
    this.saving = true;
    this.api
      .updateFormField(this.bookingLinkId, item.id, {
        fieldName: item.fieldName,
        fieldLabel: item.fieldLabel,
        fieldType: item.fieldType,
        isRequired: item.isRequired,
        sortOrder: item.sortOrder,
        options: item.options,
        placeholder: item.placeholder,
        defaultValue: item.defaultValue,
        crmProperty: item.crmProperty,
        // undefined → el backend recibe null y limpia la regla (jsonb no acepta "")
        conditionalLogic: conditionalLogic,
        isSystem: item.isSystem,
        isActive: item.isActive,
      })
      .subscribe({
        next: () => {
          this.saving = false;
          this.editingConditionalId = null;
          this.load();
          this.toast.updated('Campo');
        },
        error: (err) => {
          this.saving = false;
          this.toast.error({ title: 'Error', message: err.error?.message || 'Error al guardar la condición' });
        },
      });
  }

  toggle(item: MeetingBookingFormFieldResponse): void {
    this.api
      .updateFormField(this.bookingLinkId, item.id, {
        fieldName: item.fieldName,
        fieldLabel: item.fieldLabel,
        fieldType: item.fieldType,
        isRequired: item.isRequired,
        sortOrder: item.sortOrder,
        options: item.options,
        placeholder: item.placeholder,
        defaultValue: item.defaultValue,
        crmProperty: item.crmProperty,
        conditionalLogic: item.conditionalLogic,
        isSystem: item.isSystem,
        isActive: !item.isActive,
      })
      .subscribe({ next: () => this.load() });
  }

  async remove(item: MeetingBookingFormFieldResponse): Promise<void> {
    if (item.isSystem) {
      await this.confirmDialog.alert({ title: 'Aviso', message: 'No se puede eliminar un campo del sistema.', variant: 'warning' });
      return;
    }
    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar campo',
      message: `¿Eliminar el campo "${item.fieldLabel}"?`
    });
    if (!ok) return;
    this.api.deleteFormField(this.bookingLinkId, item.id).subscribe({
      next: () => { this.load(); this.toast.deleted('Campo'); },
      error: (err) => { this.toast.error({ title: 'Error', message: err.error?.message || 'Error al eliminar' }); },
    });
  }
}
